import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material'
import { ResponsiveBar } from '@nivo/bar'
import React, { useState, useEffect } from 'react'
import { TopDriverMileageGraphData } from 'interfaces/IDrivers'

interface ITopDriverGraphProps {
  data: TopDriverMileageGraphData | null
  useKilometers?: boolean
}

const TopDriverGraph: React.FC<ITopDriverGraphProps> = ({ data, useKilometers = false }) => {
  const [topCount, setTopCount] = useState(5)
  const [graphData, setGraphData] = useState<any>(null)
  const distanceUnit = useKilometers ? 'km' : 'mi'
  const distanceLabel = useKilometers ? 'Kilometers' : 'Mileage'

  useEffect(() => {
    setGraphData(createGraphData())
  }, [topCount])

  const handleTopCountChange = (value: number) => {
    setTopCount(value)
  }

  const createGraphData = () => {
    if (!data?.drivers) return []

    const sortedDrivers = [...data.drivers]
      .sort((a, b) => b.miles - a.miles)
      .slice(0, topCount)

    return sortedDrivers.map((driver) => ({
      driverName: driver.name,
      mileage: driver.miles,
    }))
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Top Filter Dropdown */}
      <Box sx={{ position: 'absolute', top: -30, right: 0, zIndex: 10 }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            id="top-driver-select"
            value={topCount}
            onChange={(e) => handleTopCountChange(Number(e.target.value))}
          >
            <MenuItem value={5}>Top 5</MenuItem>
            <MenuItem value={10}>Top 10</MenuItem>
            <MenuItem value={20}>Top 20</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Chart Container */}
      <Box sx={{ width: '100%', height: '300px', mt: 5 }}>
        <ResponsiveBar
          data={graphData}
          keys={['mileage']}
          indexBy="driverName"
          margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
          padding={0.3}
          colors={['#2563eb']}
          borderRadius={4}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: topCount > 10 ? -45 : 0,
            legend: '',
            legendPosition: 'middle',
            legendOffset: 36,
            // Handle driver names when there are more than 10 bars
            renderTick:
              topCount > 10
                ? ({ value, x, y }) => (
                    <g transform={`translate(${x},${y + 15})`}>
                      <text
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{
                          fontSize: '11px',
                          fill: '#777',
                        }}
                      >
                        {value.split(' ').map((word: string, i: number) => (
                          <tspan key={i} x={0} dy={i === 0 ? 0 : '1.2em'}>
                            {word}
                          </tspan>
                        ))}
                      </text>
                    </g>
                  )
                : undefined,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '',
            legendPosition: 'middle',
            legendOffset: -50,
            format: (value) => `${value / 1000}k`,
          }}
          enableLabel={false}
          tooltip={({ id, value, indexValue }) => (
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'background.paper',
                boxShadow: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {indexValue}
              </Typography>
              <Typography variant="body2">
                {distanceLabel}: {Math.trunc(Math.abs(value || 0)).toLocaleString()} {distanceUnit}
              </Typography>
            </Box>
          )}
          theme={{
            axis: {
              legend: { text: { fontSize: 12, fill: '#777' } },
              ticks: { text: { fontSize: 12, fill: '#777' } },
            },
            grid: { line: { stroke: '#e0e0e0', strokeWidth: 1 } },
          }}
        />
      </Box>

      {/* Bottom Summary Text */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 30,
          left: 0,
          right: 0,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing the top {topCount} of {data?.totalDrivers || 0} active drivers
          by {distanceLabel.toLowerCase()}
        </Typography>
      </Box>
    </Box>
  )
}

export default TopDriverGraph
